import { useEffect, useRef, useState, useCallback } from 'react';
import { compressImage } from '@/utils/imageCompression';
import { sanitizeDescription } from '@/utils/descriptionSanitize';

export interface RichDescriptionLabels {
  label: string;
  placeholder: string;
  /** Shown while the content differs from the last save and the box is not left yet. */
  unsaved: string;
  saving: string;
  saved: string;
  error: string;
  /** The button next to `error` that tries the save again. */
  retry: string;
}

interface RichDescriptionEditorProps {
  /** HTML value for this specific task. Used to initialize the editor when taskKey changes. */
  valueHtml: string;
  /**
   * Called once when the user leaves the box (blur) with changed content, and
   * on task switch / unmount / page hide with an unsaved edit. At most one
   * call is in flight; the editor is read-only until it settles.
   */
  onSaveHtml: (html: string) => Promise<string>;
  /** Upload an image file; must return the Odoo src URL. */
  uploadImage: (file: File) => Promise<{ src: string }>;
  labels: RichDescriptionLabels;
  disabled?: boolean;
  /**
   * Opaque key that identifies "which task is displayed". The editor resets
   * its innerHTML ONLY when this key changes (not on every render), so caret
   * position and keystrokes are never interrupted by a re-render.
   */
  taskKey: string | number;
  id?: string;
}

type SaveState = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';
type TaskKey = string | number;
type Snapshot = { taskKey: TaskKey; save: (html: string) => Promise<string>; html: string };

/**
 * Rich description editor backed by a contenteditable div.
 *
 * Every save of a description posts an "omschrijving veranderd" note in Odoo
 * (Studio automation "Description logging"), so the editor saves once per edit:
 * - Typing only marks the edit unsaved. Leaving the box (blur) saves it, and
 *   only when it differs from the task's last confirmed html.
 * - At most one save is in flight, no queue. While it runs the editor is
 *   read-only (also after a switch to another task), so no edit can be made
 *   that the running save would miss.
 * - A failed save keeps that task's html; the error shows a retry button, and
 *   a revisit of the task shows the unsaved html, not the stale loaded value.
 * - An unsaved edit is flushed on task switch, unmount and page hide (close or
 *   navigate away); a failed save is retried once on unmount.
 * - paste: image item → compressImage → uploadImage → insert <img> at caret.
 *   plain text → document.execCommand insertText (strips arbitrary HTML).
 * - drop: image file → same path as paste.
 */
export function RichDescriptionEditor({
  valueHtml,
  onSaveHtml,
  uploadImage,
  labels,
  disabled = false,
  taskKey,
  id,
}: RichDescriptionEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const taskKeyRef = useRef<TaskKey>(taskKey);
  // Per task: the last html the server accepted (or the loaded html), the
  // unsaved html (and save fn) of a failed save, and whether a save succeeded
  // since load.
  const confirmedRef = useRef(new Map<TaskKey, string>());
  const failedRef = useRef(new Map<TaskKey, Snapshot>());
  const savedRef = useRef(new Set<TaskKey>());
  // The one save in flight, if any.
  const inflightRef = useRef<{ taskKey: TaskKey; html: string } | null>(null);
  // The unsaved edit, captured at INPUT time: the task, its save fn and a
  // content snapshot, so a flush never has to read the live DOM (the node may
  // already be detached mid-unmount). Null when nothing is unsaved.
  const pendingRef = useRef<Snapshot | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);

  // Saved range so we can restore caret after async upload
  const savedRangeRef = useRef<Range | null>(null);

  // Derive the status line and read-only flag from the refs, for the task
  // shown now. Completions only call this, so a save of another task never
  // sets this task's status.
  const refresh = useCallback(() => {
    if (!mountedRef.current) return;
    const key = taskKeyRef.current;
    const inflight = inflightRef.current;
    setBusy(inflight !== null);
    if (pendingRef.current?.taskKey === key) setSaveState('unsaved');
    else if (inflight?.taskKey === key) setSaveState('saving');
    else if (failedRef.current.has(key)) setSaveState('error');
    else if (savedRef.current.has(key)) setSaveState('saved');
    else setSaveState('idle');
  }, []);

  // Save one snapshot, unless a save is in flight or nothing changed.
  const commit = useCallback(async (snap: Snapshot): Promise<void> => {
    if (inflightRef.current) return;
    if (pendingRef.current?.taskKey === snap.taskKey) pendingRef.current = null;
    if (snap.html === confirmedRef.current.get(snap.taskKey)) {
      // Back at the saved text: a failed save of it no longer needs a retry.
      failedRef.current.delete(snap.taskKey);
      refresh();
      return;
    }
    inflightRef.current = { taskKey: snap.taskKey, html: snap.html };
    refresh();
    try {
      await snap.save(snap.html);
      confirmedRef.current.set(snap.taskKey, snap.html);
      failedRef.current.delete(snap.taskKey);
      savedRef.current.add(snap.taskKey);
    } catch {
      failedRef.current.set(snap.taskKey, snap);
      savedRef.current.delete(snap.taskKey);
    }
    inflightRef.current = null;
    refresh();
    // An edit made while this save ran (an image upload that finished, or a
    // task switch that could not flush) is saved now, one save after another.
    const next = pendingRef.current;
    if (next) await commit(next);
  }, [refresh]);

  // Flush the unsaved edit (task switch, unmount, page hide). Fire-and-forget:
  // we may be mid-unmount, so the result is only shown if still mounted.
  const flushPending = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending || inflightRef.current) return;
    void commit(pending);
  }, [commit]);

  // Save what the box holds now for the shown task (blur, retry button).
  const commitCurrent = useCallback(() => {
    const el = editorRef.current;
    if (!el || disabled) return;
    void commit({ taskKey: taskKeyRef.current, save: onSaveHtml, html: sanitizeDescription(el.innerHTML) });
  }, [commit, onSaveHtml, disabled]);

  // Leaving the box saves. The browser also blurs it when the whole window
  // loses focus (another app or tab, e.g. to copy text); that is not the end
  // of the edit, and saving there posts one note per switch. The edit stays
  // unsaved; the next blur inside the portal, a task switch, unmount or
  // pagehide saves it.
  const handleBlur = useCallback(() => {
    if (!document.hasFocus()) return;
    commitCurrent();
  }, [commitCurrent]);

  // ── Init on task switch ─────────────────────────────────────────────────
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    // Persist the outgoing task's unsaved edit before swapping content.
    flushPending();
    taskKeyRef.current = taskKey;
    setUploadFailed(false);
    const inflight = inflightRef.current;
    const failed = failedRef.current.get(taskKey);
    if (inflight?.taskKey === taskKey) {
      // Its save is still running; the consumer's valueHtml is updated only
      // after success, so it may be the old text: show what is being saved.
      el.innerHTML = inflight.html;
    } else if (failed !== undefined) {
      el.innerHTML = failed.html;
    } else {
      el.innerHTML = sanitizeDescription(valueHtml);
      confirmedRef.current.set(taskKey, sanitizeDescription(el.innerHTML));
      savedRef.current.delete(taskKey);
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskKey]);

  // ── Flush on unmount: the unsaved edit, then each failed save once ──────
  // While a save is in flight, its end saves a pending edit (see commit).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (inflightRef.current) return;
      const pending = pendingRef.current;
      const snaps = pending ? [pending] : [];
      for (const failed of failedRef.current.values()) {
        if (failed.taskKey !== pending?.taskKey) snaps.push(failed);
      }
      void (async () => {
        for (const snap of snaps) await commit(snap);
      })().catch(() => { /* flush is best-effort */ });
    };
  }, [commit]);

  // ── Flush when the page is closed or left with the box still focused ────
  // pagehide only: a tab or app switch (visibilitychange) is not the end of
  // the edit, and saving there would post a second note at the next blur.
  useEffect(() => {
    const onHide = () => flushPending();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [flushPending]);

  // ── Mark the edit unsaved (typing, paste, image insert) ─────────────────
  const markDirty = useCallback(() => {
    const el = editorRef.current;
    if (!el || disabled) return;
    setUploadFailed(false);
    const key = taskKeyRef.current;
    const html = sanitizeDescription(el.innerHTML);
    // Typed back to the saved text: nothing to save.
    pendingRef.current = html === confirmedRef.current.get(key) ? null : { taskKey: key, save: onSaveHtml, html };
    refresh();
  }, [onSaveHtml, disabled, refresh]);

  // ── Image insert helpers ────────────────────────────────────────────────
  const saveCaretRange = useCallback(() => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  }, []);

  const insertImageAtCaret = useCallback((src: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    // Restore saved range if selection is outside editor
    let range: Range | null = null;
    if (sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
      range = sel.getRangeAt(0);
    } else if (savedRangeRef.current && el.contains(savedRangeRef.current.commonAncestorContainer)) {
      range = savedRangeRef.current;
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else {
      // Fall back to end of editor
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }

    const img = document.createElement('img');
    img.src = src;
    img.className = 'img-fluid';
    img.style.maxWidth = '100%';

    range.deleteContents();
    range.insertNode(img);
    // Move caret after the inserted image
    range.setStartAfter(img);
    range.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, []);

  const handleImageFile = useCallback(async (file: File) => {
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const { src } = await uploadImage(compressed);
      insertImageAtCaret(src);
      // The editor has focus again: the image is saved when the box is left.
      markDirty();
    } catch (err) {
      console.error('[RichDescriptionEditor] image upload failed:', err);
      setUploadFailed(true);
    } finally {
      setUploading(false);
    }
  }, [uploadImage, insertImageAtCaret, markDirty]);

  // ── Paste handler ───────────────────────────────────────────────────────
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    if (disabled || busy) return;

    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find((item) => item.type.startsWith('image/'));

    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) void handleImageFile(file);
      return;
    }

    // No image — insert only plain text (strip arbitrary pasted HTML).
    // execCommand('insertText') is deprecated but still the most reliable way
    // to insert text at the caret in a contenteditable in modern browsers.
    // jsdom does not support it; we guard and fall back to a Range insert.
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    if (text) {
      if (typeof document.execCommand === 'function') {
        document.execCommand('insertText', false, text);
      } else {
        // Fallback for environments that don't support execCommand (jsdom, future).
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      markDirty();
    }
  }, [disabled, busy, handleImageFile, markDirty]);

  // ── Drop handler ────────────────────────────────────────────────────────
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (disabled || busy) return;
    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((f) => f.type.startsWith('image/'));
    if (imageFile) {
      e.preventDefault();
      void handleImageFile(imageFile);
    }
    // Non-image drops: let browser default handle them (text, links, etc.)
  }, [disabled, busy, handleImageFile]);

  const readOnly = disabled || busy;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        {id
          ? <label htmlFor={id} className="text-[10px] font-bold uppercase tracking-wide text-td-muted">{labels.label}</label>
          : <span className="text-[10px] font-bold uppercase tracking-wide text-td-muted">{labels.label}</span>
        }
        <span className="text-[11px] text-td-muted" aria-live="polite">
          {uploading && <span>{labels.saving}</span>}
          {!uploading && uploadFailed && <span className="text-td-magenta">{labels.error}</span>}
          {!uploading && !uploadFailed && saveState === 'unsaved' && labels.unsaved}
          {!uploading && !uploadFailed && saveState === 'saving' && labels.saving}
          {!uploading && !uploadFailed && saveState === 'saved' && labels.saved}
          {!uploading && !uploadFailed && saveState === 'error' && (
            <span className="text-td-magenta">
              {labels.error}{' '}
              {!disabled && (
                <button type="button" className="underline" disabled={busy} onClick={commitCurrent}>
                  {labels.retry}
                </button>
              )}
            </span>
          )}
        </span>
      </div>
      <div
        ref={editorRef}
        id={id}
        role="textbox"
        aria-multiline="true"
        aria-label={labels.label}
        aria-readonly={readOnly}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        data-placeholder={labels.placeholder}
        onInput={markDirty}
        onBlur={handleBlur}
        onMouseUp={saveCaretRange}
        onKeyUp={saveCaretRange}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()} // allow drop
        className={[
          'min-h-[4rem] w-full rounded-lg border border-gray-200 p-2 text-sm text-td-navy',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-td-blue',
          'empty:before:content-[attr(data-placeholder)] empty:before:text-td-muted',
          '[&_img]:max-w-full [&_img]:rounded [&_img]:my-1',
          disabled ? 'bg-gray-50 cursor-default' : '',
          busy && !disabled ? 'opacity-70' : '',
        ].join(' ')}
      />
    </div>
  );
}
